import { useMutation } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { authApi, useAuthState, type AuthUser } from '@/api/auth'
import { usePersonalSettings, useSavePersonalSettings } from '@/api/settings'
import { Badge, Button, Card, ErrorText, Input, PageSpinner, Select } from '@/components/ui'

import { SecurityPage } from '../security/SecurityPage'

function EmailChangeForm({ currentEmail }: { currentEmail: string | null }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const change = useMutation({
    mutationFn: (email: string) => authApi.startEmailChange(email, password),
    onSuccess: () => setPassword(''),
  })
  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (change.isPending) return
    const value = email.trim().toLowerCase()
    if (password && value && value !== currentEmail?.toLowerCase()) change.mutate(value)
  }

  return (
    <Card title="Сменить email" subtitle="Текущий адрес останется действующим, пока ты не подтвердишь новый.">
      <form className="grid gap-4 sm:max-w-sm" onSubmit={submit}>
        <Input label="Новый email" type="email" autoComplete="email" required value={email}
          onChange={(event) => setEmail(event.target.value)} disabled={change.isPending} />
        <Input label="Текущий пароль" type="password" autoComplete="current-password" required value={password}
          onChange={(event) => setPassword(event.target.value)} disabled={change.isPending} />
        <ErrorText error={change.error} />
        {change.isSuccess ? (
          <p role="status" className="text-sm text-muted break-words">
            Запрошено подтверждение адреса {change.variables}. Открой ссылку из письма в течение 24 часов.
            Если письма нет, проверь спам или обратись к администратору для проверки отправки.
          </p>
        ) : null}
        <div>
          <Button type="submit" variant="primary" loading={change.isPending}
            disabled={!password || !email.trim() || email.trim().toLowerCase() === currentEmail?.toLowerCase()}>
            Подтвердить новый email
          </Button>
        </div>
      </form>
    </Card>
  )
}

const SEX_OPTIONS = [
  { value: '', label: 'Как в InBody' },
  { value: 'male', label: 'Мужской' },
  { value: 'female', label: 'Женский' },
]

/** Пол: фигура персонажа и карт мышц, нормы силы, VO2max и жира. */
function SexField() {
  const settings = usePersonalSettings()
  const save = useSavePersonalSettings()
  const data = settings.data
  const hint =
    data?.sex == null
      ? data?.body_sex_source === 'inbody'
        ? `Из последнего InBody: ${data.body_sex === 'female' ? 'женский' : 'мужской'}`
        : 'Замеров InBody нет — фигура мужская'
      : 'Для фигуры персонажа и норм характеристик'
  return (
    <div className="min-w-0 sm:col-span-2 sm:max-w-sm">
      <Select
        label="Пол"
        hint={hint}
        options={SEX_OPTIONS}
        value={data?.sex ?? ''}
        disabled={!data || save.isPending}
        onChange={(event) => {
          const value = event.target.value
          save.mutate({ sex: value === 'male' || value === 'female' ? value : null })
        }}
      />
      <ErrorText error={save.error ?? settings.error} />
    </div>
  )
}

function PersonalDetails({ user }: { user: AuthUser }) {
  return (
    <Card title="Личные данные" actions={<Badge>{user.role === 'admin' ? 'Администратор' : 'Пользователь'}</Badge>}>
      <dl className="grid gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <dt className="text-sm text-muted">Имя</dt>
          <dd className="mt-1 break-words font-medium">{user.name || 'Не указано'}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-sm text-muted">Email</dt>
          <dd className="mt-1 break-words font-medium">{user.email || 'Не указан'}</dd>
          {user.email ? <dd className="mt-1 text-sm text-muted">{user.requires_email ? 'Ожидает подтверждения' : 'Подтверждён'}</dd> : null}
        </div>
        <SexField />
      </dl>
      <Link to="/settings/data" className="mt-4 inline-flex min-h-11 items-center text-sm text-accent underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
        Скачать мои данные
      </Link>
    </Card>
  )
}

export function ProfilePage() {
  const auth = useAuthState()
  if (auth.isPending) return <PageSpinner />
  if (!auth.data?.user) return <ErrorText error={auth.error}>Не удалось загрузить профиль. Обнови страницу.</ErrorText>
  const user = auth.data.user

  return (
    <div className="grid gap-6">
      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <PersonalDetails user={user} />
        <EmailChangeForm key={user.id} currentEmail={user.email} />
      </div>
      <SecurityPage key={user.id} />
    </div>
  )
}
